<template>
  <div class="relative">
    <!-- Theme Toggle Button -->
    <button
      @click="toggleTheme"
      :disabled="isLoading"
      class="flex items-center justify-center w-10 h-10 rounded-lg bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-opacity-50"
      :title="getTooltip()"
    >
      <component 
        :is="getIconComponent()" 
        class="w-5 h-5 text-gray-600 dark:text-gray-300 transition-transform duration-200"
        :class="{ 'animate-spin': isLoading }"
      />
    </button>

    <!-- Theme Options Dropdown (optional, for advanced users) -->
    <div
      v-if="showDropdown"
      class="absolute right-0 top-12 mt-1 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1 z-50"
    >
      <button
        v-for="option in themeOptions"
        :key="option.value"
        @click="selectTheme(option.value)"
        class="flex items-center w-full px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
        :class="{ 'bg-primary-50 dark:bg-primary-900 text-primary-700 dark:text-primary-300': theme === option.value }"
      >
        <component :is="option.icon" class="w-4 h-4 mr-3" />
        <span>{{ option.label }}</span>
        <CheckIcon v-if="theme === option.value" class="w-4 h-4 ml-auto text-primary-600 dark:text-primary-400" />
      </button>
    </div>

    <!-- Overlay to close dropdown -->
    <div
      v-if="showDropdown"
      @click="showDropdown = false"
      class="fixed inset-0 z-40"
    ></div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { Sun, Moon, Monitor, Check as CheckIcon, Loader2 } from 'lucide-vue-next'
import { useThemeStore } from '@/stores/theme'
import type { Theme } from '@/stores/theme'

interface Props {
  showDropdown?: boolean
  size?: 'sm' | 'md' | 'lg'
}

const props = withDefaults(defineProps<Props>(), {
  showDropdown: false,
  size: 'md'
})

const themeStore = useThemeStore()
const showDropdown = ref(false)

const theme = computed(() => themeStore.theme)
const isLoading = computed(() => themeStore.isLoading)

const themeOptions = [
  { value: 'light' as Theme, label: 'Light Mode', icon: Sun },
  { value: 'dark' as Theme, label: 'Dark Mode', icon: Moon },
  { value: 'system' as Theme, label: 'System', icon: Monitor }
]

const getIconComponent = () => {
  if (isLoading.value) return Loader2
  
  switch (theme.value) {
    case 'light':
      return Sun
    case 'dark':
      return Moon
    case 'system':
      return Monitor
    default:
      return Monitor
  }
}

const getTooltip = () => {
  if (isLoading.value) return 'Updating theme...'
  
  switch (theme.value) {
    case 'light':
      return 'Switch to Dark Mode'
    case 'dark':
      return 'Switch to System'
    case 'system':
      return 'Switch to Light Mode'
    default:
      return 'Toggle Theme'
  }
}

const toggleTheme = async () => {
  if (props.showDropdown) {
    showDropdown.value = !showDropdown.value
  } else {
    try {
      await themeStore.cycleTheme()
    } catch (error) {
      console.error('Failed to change theme:', error)
    }
  }
}

const selectTheme = async (selectedTheme: Theme) => {
  showDropdown.value = false
  try {
    await themeStore.setTheme(selectedTheme)
  } catch (error) {
    console.error('Failed to change theme:', error)
  }
}
</script>

<style scoped>
/* Custom animations for smooth theme transitions */
.theme-transition {
  transition: background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease;
}

/* Focus styles that work in both themes */
.focus\:ring-primary-500:focus {
  --tw-ring-color: rgb(59 130 246 / 0.5);
}

.dark .focus\:ring-primary-500:focus {
  --tw-ring-color: rgb(96 165 250 / 0.5);
}
</style>
